<script>
  import { usePageContext } from "./pageContext.js";
  import BackButton from "./BackButton.svelte";
  import User from "../layout/header/User.svelte";
  import { translate } from "../../i18n/i18next.js";
  import HelpIconButton from "../layout/header/HelpIconButton.svelte";
  import CommentDialog from "./CommentDialog.svelte"
  import { onMount } from "svelte";
  import { fetchCommentData } from "../../utils/getData.js"
  import { formatDateTime, deprecatedDateTimeString2DateTimeString } from "../../utils/dateFormatter.js"

  /**
   * @type {string|null}
   */
  export let backTo = "/";
  export let params;

  const { context } = usePageContext();

  let showCommentPanel = false;
  let buttonRect;
  let commentPanelLeft;

  let commentsPromise;
  $: projectId = $params.project;

  $: if (projectId) {
    commentsPromise = fetchCommentData(projectId);
  }

  let isNewCommentIn = false;

  function handleClick(event) {
    showCommentPanel = !showCommentPanel;
    if (showCommentPanel) {
      buttonRect = event.target.getBoundingClientRect();
      commentPanelLeft = buttonRect.left + buttonRect.width / 2 - 140;
      localStorage.setItem(`comment-last-viewed-date-${projectId}`, new Date().getTime().toString());
      isNewCommentIn = false;
    }
  }

  let commentDialog
  function onClickCommentDetail() {
    commentDialog.showModal();
    commentDialog.scrollTop = 0;
  }

  onMount(() => {
    const handleClickOutside = (event) => {
      if (showCommentPanel && !event.target.closest("#comment-panel") && !event.target.closest("#commentButton")) {
        showCommentPanel = false;
        commentDialog.close();
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  });


</script>

<article id="root">
  <div id="headline-wrap">
    <div id="headline-container">
      <div id="headline-left">
        {#if backTo}<BackButton {backTo} />{/if}
      </div>
      <div id="headline-middle">
        <img src="/icon-192.png" alt="Asprova My Schedule Icon" />
        <span class="header-title">
          {$context.headerTitle}
          <span id="header-title-upload-time" style="font-size:large; font-weight:normal; color:gray; margin-left:10px">
            {$context.uploadTime
              ? `${formatDateTime(deprecatedDateTimeString2DateTimeString($context.uploadTime))} ${translate("generic.upload")}`
              : ""}
          </span>
        </span>
        {#if projectId}
         <!-- svelte-ignore a11y-click-events-have-key-events -->
          <img
          src={isNewCommentIn ? "/assets/icons/icon_comment_new.svg":"/assets/icons/icon_comment_normal.svg"}
          alt="Comment Icon"
          style="cursor: pointer;"
          on:click={handleClick}
          id="commentButton"
          />
        {/if}
       
      </div>
      <div id="headline-right">
        {#if $context.rightPane.href}<HelpIconButton />{/if}
        <User />
      </div>
    </div>
  </div>


  <div id="comment-panel" style={`left: ${commentPanelLeft}px; display: ${showCommentPanel ? 'flex' : 'none'}`}>
    {#await commentsPromise}
      <div class="panel-content">
        <div class="comment-text">Loading...</div>
      </div>
    {:then comments} 
      {#if !comments || comments.Comments.length === 0}
        <div class="panel-content">
          <div class="comment-text">{translate("frontend:generic.noComments")}</div>
        </div>
      {:else}
        {(() => {
          const newestCommentDatetime = comments.Comments[0]['Timestamp'];
          let newestCommentDateUnixTime = new Date(newestCommentDatetime).getTime();

          const lastViewedDateUnixTimeInString = localStorage.getItem(`comment-last-viewed-date-${projectId}`);
          if (!lastViewedDateUnixTimeInString || Number(lastViewedDateUnixTimeInString) < newestCommentDateUnixTime) {
            isNewCommentIn = true;
          }
          return '';
        })()}
        <div class="panel-content">
          <div class="username">{comments.Comments[0]['Username']}</div>
          <div class="timestamp">{new Date(comments.Comments[0]['Timestamp']).toLocaleString()}</div>
          <div class="comment-text">{comments.Comments[0]['Content']}</div>
        </div>
        <button class="detail-button" on:click={onClickCommentDetail}>
          <div class="dot"></div>
          <div class="dot"></div>
          <div class="dot"></div>
        </button>
      {/if}
      {:catch error}
      <div class="panel-content">
        <div class="comment-text">Error loading comments: {error.message}</div>
      </div>
    {/await}
    
  </div>

  <CommentDialog
    bind:dialog={commentDialog}
    {commentsPromise}
    onClose={()=>commentDialog.close()}
  />
</article>

<svelte:head>
  <title>{$context.headerTitle} - Asprova My Schedule</title>
</svelte:head>

<style>
  #root {
    height: 50px;
    position: sticky;
    top: 0;
    z-index: 100;
  }
  #headline-wrap {
    width: 100%;
    background-color: #e9e9f0;
    border-bottom: solid #cdcdcd;
  }
  #headline-container {
    width: 95%;
    height: 100%;
    margin: auto;
    display: flex;
    justify-content: space-between;
  }
  #headline-left {
    flex-basis: content;
    display: flex;
    justify-content: start;
    align-items: center;
  }
  #headline-middle {
    container-type: inline-size;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }
  #headline-middle > img {
    height: 48px;
    aspect-ratio: 1;
  }
  #headline-right {
    flex-basis: content;
    display: flex;
    justify-content: end;
    align-items: center;
    gap: 1rem;
  }
  .header-title {
    font-size: x-large;
    font-weight: bold;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  @container (width < 200px) {
    .header-title {
      display: none;
    }
  }

  @container (width < 700px) {
    #header-title-upload-time {
      display: none;
    }
  }

  #commentButton {
    height: 32px !important;
    aspect-ratio: 1;
  }

  #commentButton:hover {
    background-color: #CCCCCC;
  }

  #commentButton:active {
    background-color: #DDDDDD;
    transform: translateY(1px);
  }

  #comment-panel {
    position: absolute;
    top: 50px;
    width: 280px;
    max-height: 170px;
    background-color: #f0f0f0;
    border: 1px solid #ccc;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    z-index: 101;
    display: flex;
    overflow: hidden; 
  }

  .panel-content {
    padding: 16px;
    flex-grow: 1; 
    overflow: hidden; 
    display: flex;
    flex-direction: column; 
  }

  .username {
    font-weight: bold;
    font-size: 16px;
    margin-bottom: 8px;
    white-space: nowrap; 
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .timestamp {
    font-size: 14px;
    color: gray;
    margin-bottom: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .comment-text {
    font-size: 16px;
    white-space: pre-wrap; 
    line-height: 1.4;
    word-wrap: break-word;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3; 
    line-clamp: 3; 
    -webkit-box-orient: vertical;
  }

  .detail-button {
    position: absolute;
    top: 5px;
    right: 5px;
    background: none;
    border: none;
    padding: 5px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
    height: 25px; 
    width: 20px; 
  }

  .dot {
    width: 3px;
    height: 3px;
    background-color: #00468F;
    border-radius: 50%;
  }
</style>